import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Star, MapPin, Briefcase, Brain, RefreshCw, Lock, Sparkles, ChevronRight, User, Crown, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { MembershipBadge, DocumentVerifiedBadge } from "../components/MembershipBadge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ScoreRing = ({ score }) => {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative w-20 h-20 flex items-center justify-center">
      <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={radius} fill="none" stroke="#e7e5e4" strokeWidth="4" />
        <motion.circle
          cx="40" cy="40" r={radius} fill="none" stroke="#78716c" strokeWidth="4"
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold text-stone-700">{score}</span>
        <span className="text-[9px] text-stone-400 tracking-wide">MATCH</span>
      </div>
    </div>
  );
};

const MatchCard = ({ match, index, membership }) => {
  const age = match.birth_year ? 2026 - match.birth_year : "?";
  const isBlurred = membership === "regular";
  const initial = match.username?.[0] || "?";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
      className="bg-white rounded-xl border border-stone-200 overflow-hidden hover:border-stone-300 transition-all duration-300"
      data-testid={`match-card-${index}`}
    >
      <div className="p-5">
        <div className="flex gap-4 items-start">
          {/* Score Ring */}
          <div className="flex-shrink-0">
            <ScoreRing score={match.compatibility_score} />
          </div>

          {/* Profile Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-9 h-9 rounded-full bg-stone-200 flex items-center justify-center border border-stone-300">
                <span className="text-stone-600 font-semibold text-sm">{initial}</span>
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <span className={`font-semibold text-stone-800 ${isBlurred ? "blur-sm select-none" : ""}`}>
                    {match.username}
                  </span>
                  <MembershipBadge membership={match.membership_type} size="sm" />
                </div>
                <div className="flex items-center gap-2 text-xs text-stone-400 mt-0.5">
                  {(match.country || match.city) && (
                    <span className="flex items-center gap-0.5">
                      <MapPin className="w-3 h-3" />
                      {[match.country, match.city].filter(Boolean).join(" ")}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-stone-500">
              <span>{age}세 · {match.gender === "male" ? "남성" : match.gender === "female" ? "여성" : ""}</span>
              {match.job && (
                <span className="flex items-center gap-0.5">
                  <Briefcase className="w-3 h-3" /> {match.job}
                </span>
              )}
              {match.mbti && (
                <span className="flex items-center gap-0.5">
                  <Brain className="w-3 h-3" /> {match.mbti}
                </span>
              )}
              {match.height && <span>{match.height}cm</span>}
            </div>
          </div>
        </div>

        {/* Match Reasons */}
        <div className="mt-3.5 pt-3.5 border-t border-stone-100">
          <div className="flex flex-wrap gap-1.5">
            {match.match_reasons?.map((reason, i) => (
              <span
                key={i}
                className="px-2.5 py-1 bg-stone-100 text-stone-600 text-xs rounded-md"
              >
                {reason}
              </span>
            ))}
          </div>
        </div>

        {/* Advice */}
        {match.advice && (
          <div className="mt-3 p-3 bg-stone-50 rounded-lg">
            <p className="text-xs text-stone-500 leading-relaxed">
              <span className="font-medium text-stone-600">TIP</span> &mdash; {match.advice}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default function SmartMatchPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMatches = async (refresh = false) => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const url = refresh
        ? `${API_URL}/api/matching/refresh`
        : `${API_URL}/api/matching/recommendations`;
      const method = refresh ? "POST" : "GET";
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        setError("premium_required");
        return;
      }
      if (!res.ok) throw new Error("매칭 조회에 실패했습니다");
      const data = await res.json();
      setMatches(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && token) {
      fetchMatches();
    }
  }, [user, token]);

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-white pt-24 flex items-center justify-center" data-testid="smart-match-page">
        <div className="text-center p-8">
          <Lock className="w-10 h-10 text-stone-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-stone-700 mb-2">로그인이 필요합니다</h2>
          <p className="text-stone-400 text-sm">AI 스마트 매칭을 이용하려면 로그인해주세요.</p>
        </div>
      </div>
    );
  }

  // Basic membership restriction
  if (error === "premium_required" || (user.membership_type === "basic")) {
    return (
      <div className="min-h-screen bg-white pt-24" data-testid="smart-match-page">
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-2xl p-10 border border-stone-200">
            <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-8 h-8 text-stone-400" />
            </div>
            <h2 className="text-xl font-bold text-stone-800 mb-2">AI 스마트 매칭</h2>
            <p className="text-stone-400 text-sm mb-8">
              AI가 회원님의 프로필을 분석하여<br />
              가장 호환성이 높은 분을 추천해드립니다.
            </p>
            <div className="bg-stone-50 rounded-xl p-4 mb-8 text-left space-y-3">
              <div className="flex items-center gap-3 text-sm text-stone-600">
                <div className="w-7 h-7 bg-stone-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-3.5 h-3.5 text-stone-500" />
                </div>
                <span><strong>일반회원</strong> — 하루 1명 추천 (일반회원 중에서)</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-stone-600">
                <div className="w-7 h-7 bg-stone-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="w-3.5 h-3.5 text-stone-500" />
                </div>
                <span><strong>정회원</strong> — 매일 3명 추천</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-stone-600">
                <div className="w-7 h-7 bg-stone-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <Star className="w-3.5 h-3.5 text-stone-500" />
                </div>
                <span><strong>프리미엄</strong> — 매일 5명 추천</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-stone-600">
                <div className="w-7 h-7 bg-stone-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <Crown className="w-3.5 h-3.5 text-stone-500" />
                </div>
                <span><strong>VIP</strong> — 매일 5명 + 상세 분석</span>
              </div>
            </div>
            <Button
              onClick={() => navigate("/membership")}
              className="bg-stone-800 hover:bg-stone-900 text-white px-8 py-3 rounded-full text-sm"
              data-testid="upgrade-membership-btn"
            >
              멤버십 업그레이드
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-24 pb-16" data-testid="smart-match-page">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-stone-500" />
            </div>
            <span className="text-xs font-medium text-stone-400 uppercase tracking-widest">AI 맞춤 추천</span>
            <span className="px-1.5 py-0.5 bg-stone-100 text-stone-500 text-[10px] font-bold rounded tracking-wider">SMART</span>
          </div>
          <p className="text-sm text-stone-400 mt-2">
            {user?.username}님과 잘 맞을 것 같은 회원이에요
          </p>
        </motion.div>

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20" data-testid="matching-loading">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 rounded-full border-2 border-stone-200 border-t-stone-500 mb-4"
            />
            <p className="text-stone-500 text-sm font-medium">AI가 최적의 매칭을 분석중입니다...</p>
            <p className="text-stone-300 text-xs mt-1">잠시만 기다려주세요 (10~20초 소요)</p>
          </div>
        )}

        {/* Error */}
        {error && error !== "premium_required" && (
          <div className="text-center py-20" data-testid="matching-error">
            <p className="text-stone-500 mb-4 text-sm">{error}</p>
            <Button onClick={() => fetchMatches()} variant="outline" size="sm">다시 시도</Button>
          </div>
        )}

        {/* Results */}
        {!loading && matches && (
          <>
            <div className="flex items-center justify-between mb-5">
              <div className="text-xs text-stone-400">
                <span className="font-medium text-stone-600">{matches.matches?.length || 0}명</span> 추천
                <span className="mx-1.5 text-stone-200">|</span>
                전체 <span className="font-medium text-stone-600">{matches.total_candidates}명</span> 중 선별
              </div>
              <Button
                onClick={() => fetchMatches(true)}
                variant="outline"
                size="sm"
                className="gap-1.5 text-xs border-stone-200 text-stone-500 hover:text-stone-700 hover:bg-stone-50"
                disabled={loading}
                data-testid="refresh-matches-btn"
              >
                <RefreshCw className="w-3 h-3" />
                새로고침
              </Button>
            </div>

            {matches.matches?.length === 0 ? (
              <div className="text-center py-16 bg-stone-50 rounded-xl border border-stone-100">
                <Heart className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <h3 className="text-sm font-semibold text-stone-600 mb-1">아직 매칭 결과가 없습니다</h3>
                <p className="text-stone-400 text-xs">더 많은 회원이 가입하면 매칭이 시작됩니다.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <AnimatePresence>
                  {matches.matches.map((match, index) => (
                    <MatchCard
                      key={match.candidate_id}
                      match={match}
                      index={index}
                      membership={user.membership_type}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}

            {/* Footer hint */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-8 flex items-center justify-between px-4 py-3 bg-stone-50 rounded-lg"
            >
              <p className="text-[11px] text-stone-400">
                프로필을 완성하면 더 정확한 추천을 받을 수 있어요
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/profile")}
                className="text-[11px] h-7 px-3 border-stone-200 text-stone-500 hover:text-stone-700"
              >
                프로필 수정
              </Button>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
}
